import moment from 'moment';
import { SORT_DOWN, SORT_UP } from '../../utils/Array.extensions';
import { ga4LabelColumn, ga4RowKind, isNotApplicableCell } from './channelApplicability';

/**
 * El orden de las filas de `DataExplorerTable` y el agrupado de la primera columna, PUROS: los usa la
 * tabla en pantalla y el PDF de Informes (`utils/pdfExport.js`), que tienen que salir iguales.
 *
 * Dos modos. Sin `stableOrder` es el orden de siempre del Explorador: el de la columna clicada, sin
 * desempate. Con `stableOrder` (Informes y su portal, tanda iVirtual 2026-10-21):
 *  - si no hay orden elegido, o el elegido es de una columna que ya no está (se cambiaron las columnas
 *    de la tabla), se ordena por la PRIMERA dimensión: las fechas de la más nueva a la más vieja y el
 *    resto de la A a la Z. Hasta acá la tabla de un panel salía en el orden crudo del motor, porque
 *    el orden por defecto sólo se fijaba cuando los datos llegaban DESPUÉS de montarla;
 *  - las filas que empatan se desempatan por las demás dimensiones, de izquierda a derecha y siempre
 *    ascendentes: «15/10 GOOGLE» y «15/10 FACEBOOK» ya no cambian de lugar de un día al otro.
 */

const DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;
// Día, Mes y Semana llegan del Data Node como `YEAR_MONTH_DAY` (`settings.js`, DATA_TYPE_DATE).
const TIME_DATA_TYPES = new Set(['DATE', 'YEAR_MONTH_DAY']);

const isMetricKey = (key) => !!key && key.startsWith('metric_');

/** El valor con el que se ordena una celda (el mismo que leía `DataExplorerTable.getCellValue`). */
export function cellSortValue(value) {
    if (typeof value === 'object' && value !== null && value.currentValue !== undefined) return value.currentValue;
    if (typeof value === 'object' && value !== null && value.value !== undefined) return value.value;
    return value;
}

function compareValues(header, valA, valB) {
    const isDateColumn = header.dataType === 'DATE'
        || (typeof valA === 'string' && DATE_PATTERN.test(valA))
        || (typeof valB === 'string' && DATE_PATTERN.test(valB));
    if (isDateColumn) {
        const dateA = moment(valA, 'DD/MM/YYYY');
        const dateB = moment(valB, 'DD/MM/YYYY');
        if (dateA.isValid() && dateB.isValid()) {
            if (dateA.isBefore(dateB)) return -1;
            return dateA.isAfter(dateB) ? 1 : 0;
        }
    }
    if (valA < valB) return -1;
    return valA > valB ? 1 : 0;
}

/**
 * El comparador del clic en un encabezado, tal cual estaba en `DataExplorerTable.render`: una celda
 * que no aplica («—») o vacía va al final en las dos direcciones.
 */
export function compareRowsBy(header, direction, fieldsData) {
    return (a, b) => {
        const naA = isNotApplicableCell(a, header.key, fieldsData);
        const naB = isNotApplicableCell(b, header.key, fieldsData);
        if (naA || naB) return naA === naB ? 0 : (naA ? 1 : -1);

        const valA = cellSortValue(a[header.key]);
        const valB = cellSortValue(b[header.key]);
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        const result = compareValues(header, valA, valB);
        return direction === SORT_UP ? result : -result;
    };
}

/** Lo mismo, pero dos vacías empatan: es lo que deja desempatar por la columna siguiente. */
function stableCompareBy(header, direction, fieldsData) {
    const compare = compareRowsBy(header, direction, fieldsData);
    return (a, b) => {
        const emptyA = cellSortValue(a[header.key]) == null;
        const emptyB = cellSortValue(b[header.key]) == null;
        return emptyA && emptyB ? 0 : compare(a, b);
    };
}

function isTimeColumn(header, rows) {
    if (TIME_DATA_TYPES.has(header.dataType)) return true;
    const sample = (rows || []).map((row) => cellSortValue(row[header.key])).find((value) => value != null);
    return typeof sample === 'string' && DATE_PATTERN.test(sample);
}

/** La primera dimensión: las dimensiones van siempre antes que las métricas. */
export function firstDimensionOf(headers) {
    return (headers || []).find((header) => !isMetricKey(header.key)) || null;
}

/** Hacia dónde arranca una columna: métricas y fechas de mayor a menor, el texto de la A a la Z. */
export function naturalDirection(header, rows) {
    if (isMetricKey(header.key) || isTimeColumn(header, rows)) return SORT_DOWN;
    return SORT_UP;
}

/** El orden con el que arranca la tabla en Informes: la primera dimensión, la fecha más nueva primero. */
export function defaultTableOrdering(headers, rows) {
    const first = firstDimensionOf(headers);
    if (!first) return { columnId: null, direction: SORT_DOWN };
    return { columnId: first.key, direction: naturalDirection(first, rows) };
}

/** El orden que vale: el elegido si su columna sigue en la tabla; si no (o sin elegir), el de arranque. */
export function effectiveTableOrdering(ordering, headers, rows) {
    const chosen = ordering && ordering.columnId && (headers || []).some((header) => header.key === ordering.columnId);
    return chosen ? ordering : defaultTableOrdering(headers, rows);
}

/**
 * Las filas ordenadas, en un arreglo nuevo. Sin columna (o con una que no está), en el orden en que
 * llegaron. `tieBreak` desempata por las otras dimensiones (sólo en `stableOrder`).
 */
export function sortTableRows(rows, headers, ordering, fieldsData, { tieBreak = false } = {}) {
    const sorted = [...(rows || [])];
    const header = ordering && ordering.columnId ? (headers || []).find((h) => h.key === ordering.columnId) : null;
    if (!header) return sorted;
    if (!tieBreak) return sorted.sort(compareRowsBy(header, ordering.direction, fieldsData));

    const comparators = [
        stableCompareBy(header, ordering.direction, fieldsData),
        ...headers.filter((h) => !isMetricKey(h.key) && h.key !== header.key)
            .map((h) => stableCompareBy(h, SORT_UP, fieldsData)),
    ];
    return sorted.sort((a, b) => {
        for (const compare of comparators) {
            const result = compare(a, b);
            if (result !== 0) return result;
        }
        return 0;
    });
}

/**
 * «Agrupar por la primera columna»: los índices de las filas (ya ordenadas) cuya primera celda repite
 * la de la fila de arriba y va en blanco, como en Looker Studio. Sólo con 2+ dimensiones y la tabla
 * ordenada por la primera: ordenada por otra columna, la tabla queda plana. La fila de GA4 cuenta como
 * su propio valor («GA4 · sin campaña de pauta» no es lo mismo que «GA4»), así que nunca se borra su rótulo.
 */
export function repeatedFirstColumnRows(sortedRows, headers, ordering, fieldsData) {
    const repeated = new Set();
    const first = headers && headers[0];
    const dimensions = (headers || []).filter((header) => !isMetricKey(header.key));
    if (!first || isMetricKey(first.key) || dimensions.length < 2) return repeated;
    if (!ordering || ordering.columnId !== first.key) return repeated;

    const isGa4Column = ga4LabelColumn(headers) === first.key;
    const keyOf = (row) => {
        const kind = isGa4Column ? ga4RowKind(row, headers, fieldsData) : null;
        return kind ? `ga4:${kind}` : String(cellSortValue(row[first.key]) ?? '');
    };
    (sortedRows || []).forEach((row, index) => {
        if (index > 0 && keyOf(row) === keyOf(sortedRows[index - 1])) repeated.add(index);
    });
    return repeated;
}
